import {
  AfterViewInit,
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DashboardService } from '../../services/dashboard.service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-view-classification',
  templateUrl: './view-classification.component.html',
  styleUrl: './view-classification.component.scss',
})
export class ViewClassificationComponent implements AfterViewInit, OnInit, OnDestroy {
  displayedColumns: string[] = [
    'database',
    'schema',
    'table',
    'createdBy',
    'createdOn',
    'actions',
  ];

  dataSource = new MatTableDataSource<any>([]);

  files: any[] = [];
  selectedFileName = '';
  classificationDetail: any;
  private dialogRef?: MatDialogRef<any>;

  @ViewChild('descriptionDialog') descriptionDialog!: TemplateRef<any>;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  private savedListSubscription!: Subscription;
  private classificationDetailSubscription!: Subscription;
  private downloadFileClassificationDetailSubscription!: Subscription;

  constructor(
    public dialog: MatDialog,
    private dashboardService: DashboardService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.savedListSubscription = this.dashboardService.getSavedList().subscribe({
      next: (response: any) => {
        this.files = response?.status === 'success' ? response.files ?? [] : [];
        this.dataSource.data = this.files;
        if (this.paginator) this.dataSource.paginator = this.paginator;
        if (this.sort) this.dataSource.sort = this.sort;

        this.cdr.markForCheck();
      },
      error: () => {
        this.files = [];
        this.dataSource.data = [];
        this.toastr.error('Failed to load saved classifications', 'Error');
        this.cdr.markForCheck();
      },
    });
  }

  ngAfterViewInit(): void {
    if (this.paginator) this.dataSource.paginator = this.paginator;
    if (this.sort) this.dataSource.sort = this.sort;
  }

  viewClassification(filename: string) {
    this.selectedFileName = filename;
    this.classificationDetailSubscription = this.dashboardService
      .getFileClassificationDetails(filename)
      .subscribe((response: any) => {
        this.classificationDetail = response?.data?.metadata;
        this.dialogRef = this.dialog.open(this.descriptionDialog, {
          width: '800px',
          maxWidth: '90vw',
          height: 'auto',
          maxHeight: '90vh',
          disableClose: true,
          panelClass: 'description-dialog-panel',
        });
      });
  }

  downloadClassification(filename: string) {
    this.downloadFileClassificationDetailSubscription = this.dashboardService.downloadFileClassificationDetail(filename).subscribe({
      next: (res) => {
        const xlsxBlob = new Blob([res.body!], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const contentDisposition = res.headers.get('Content-Disposition');
        const fileName =
          this.extractFileName(contentDisposition) || 'report.xlsx';

        const url = URL.createObjectURL(xlsxBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        this.dialogRef?.close();
        this.toastr.success('CSV file downloaded successfully', 'Downloaded');
      },
      error: (err) => {
        this.toastr.error('Something went wrong', 'Error');
      },
    });
  }

  extractFileName(contentDisposition: string | null): string | null {
    if (!contentDisposition) return null;
    const match =
      /filename\*=(?:UTF-8'')?["']?([^\"';]+)["']?/i.exec(contentDisposition) ||
      /filename=["']?([^\"']+)["']?/i.exec(contentDisposition);
    return match?.[1] ?? null;
  }

  copyJSON() {
    const text = JSON.stringify(this.classificationDetail, null, 2).trim();
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text);
        this.toastr.success('Copied!');
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        ok ? this.toastr.info('Copied!') : this.toastr.error('Copy failed!');
      }
    } catch (err) {
      this.toastr.error('Copy failed!');
    }
  }

  closeDescriptionDialog(): void {
    this.dialogRef?.close();
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  ngOnDestroy(): void {
    // Unsubscribe to avoid memory leaks
    if (this.savedListSubscription) {
      this.savedListSubscription.unsubscribe();
    }
    if (this.classificationDetailSubscription) {
      this.classificationDetailSubscription.unsubscribe();
    }
    if (this.downloadFileClassificationDetailSubscription) {
      this.downloadFileClassificationDetailSubscription.unsubscribe();
    }
  }
}
